import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import api from '../../../api';
import BannerSection from './BannerSection';
import NotFound from '../../Pages/NotFound/NotFound';

const TableRenderer = ({ tableData, caption }) => {
  const normalized = useMemo(() => {
    if (!tableData) return { cols: [], rows: [] };

    if (typeof tableData === 'string') {
      try {
        const parsed = JSON.parse(tableData);
        if (parsed && typeof parsed === 'object') {
          return {
            cols: Array.isArray(parsed.cols) ? parsed.cols : [],
            rows: Array.isArray(parsed.rows) ? parsed.rows : []
          };
        }
      } catch {
        return { cols: [], rows: [] };
      }
    } else if (typeof tableData === 'object') {
      return {
        cols: Array.isArray(tableData.cols) ? tableData.cols : [],
        rows: Array.isArray(tableData.rows) ? tableData.rows : []
      };
    }

    return { cols: [], rows: [] };
  }, [tableData]);

  const { cols, rows } = normalized;

  if ((!cols.length) && (!rows.length)) {
    return <div className="content-box"><em>No table data available.</em></div>;
  }

  return (
    <div className="table-renderer">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        {caption ? <strong>{caption}</strong> : null}
      </div>

      <div style={{ border: '1px solid #e9ecef', borderRadius: 4 }}>
        <table className="table table-striped table-bordered mb-0"
       style={{ minWidth: Math.max(600, cols.length * 150) }}>

          {cols.length > 0 && (
            <thead className="table-light">
              <tr>
                {cols.map((c, ci) => (
                  <th key={ci} scope="col" style={{ whiteSpace: 'nowrap' }}>{c || ''}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.length > 0 ? rows.map((r, ri) => (
              <tr key={ri}>
                {(cols.length ? cols : r).map((_, ci) => (
                  <td key={ci} style={{ whiteSpace: 'pre-wrap' }}>
                    {(r && typeof r[ci] !== 'undefined') ? r[ci] : ''}
                  </td>
                ))}
              </tr>
            )) : (
              <tr>
                <td style={{ padding: 12 }} colSpan={Math.max(1, cols.length)}>No rows</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StudentLifeDetails = () => {
  const { miniSlug, slug } = useParams();
  const [activeTab, setActiveTab] = useState("");
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // ⭐ ICON INJECTOR — ONLY FOR <li>
const injectIconsIntoHTML = (html) => {
    if (!html) return html;

    return html
        .replace(/<ul>/g, `<ul class="dynamic-list-school">`)
        .replace(/<li>/g, `<li><i class="fa fa-check-circle-o icon-check"></i> `);
};

  // Detect Mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch API
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/api/${miniSlug}/${slug}`);
        const studentLifeSection = response.data.sections?.student_life_details;

        if (!mounted) return;

        if (studentLifeSection) {
          setNotFound(false);
          setSectionData(studentLifeSection);
          if (studentLifeSection.items?.tabs?.length > 0) {
            setActiveTab(studentLifeSection.items.tabs[0].slug);
          }
        } else {
          setNotFound(true);
          setError('Student Life section not found');
        }
      } catch (err) {
        console.error('API Error:', err);
        setNotFound(true);
        setError(err.message || 'API error');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false };
  }, [miniSlug, slug]);

  const getImageUrl = (src) => {
    if (!src) return '';
    if (/^https?:\/\//i.test(src)) return src;
    return `${process.env.REACT_APP_API_URL}/public/${src}`;
  };

  // ⭐ UPDATED RENDER CONTENT — ONLY ADDED ICON INJECTION
  const renderTabContent = (tab) => {
    if (!tab) return null;

    const tablePayload = tab.table ?? tab.table_data ?? null;

    switch (tab.content_type) {

      case 'content':
        return (
          <div className="content-box">
            <div
              dangerouslySetInnerHTML={{
                __html: injectIconsIntoHTML(tab.content || '')
              }}
            />
          </div>
        );

      case 'gallery':
        return (
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            slidesPerView={1}
            spaceBetween={30}
            pagination={{ clickable: true }}
            navigation
            className="studentLifeSwiper"
          >
            {tab.gallery?.map((src, index) => (
              <SwiperSlide key={index}>
                <img
                  src={getImageUrl(src)}
                  alt={`${tab.name} ${index + 1}`}
                  className="swiper-img rounded shadow-sm"
                  style={{ objectFit: 'cover' }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        );

      case 'both':
        return (
          <>
            <div className="content-box mb-4">
              <div
                dangerouslySetInnerHTML={{
                  __html: injectIconsIntoHTML(tab.content || '')
                }}
              />
            </div>

            {tab.gallery?.length > 0 && (
              <Swiper
                modules={[Autoplay, Pagination, Navigation]}
                slidesPerView={1}
                spaceBetween={30}
                loop={true}
                pagination={{ clickable: true }}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                navigation
                className="studentLifeSwiper"
              >
                {tab.gallery.map((src, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={getImageUrl(src)}
                      alt={`${tab.name} ${index + 1}`}
                      className="swiper-img rounded shadow-sm"
                      style={{ objectFit: 'cover' }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </>
        );

      case 'table':
        return (
          <div className='table-responsive'>
            <div className="content-box">
              <TableRenderer tableData={tablePayload} caption={tab.name} />

              {tab.content ? (
                <div
                  className="mt-3"
                  dangerouslySetInnerHTML={{
                    __html: injectIconsIntoHTML(tab.content || '')
                  }}
                />
              ) : null}
            </div>
          </div>
        );

      default:
        return (
          <div className="content-box">
            <div
              dangerouslySetInnerHTML={{
                __html: injectIconsIntoHTML(tab.content || '')
              }}
            />
          </div>
        );
    }
  };

  if (notFound) return <NotFound />;
  if (loading || error || !sectionData) return null;

  const tabs = sectionData.items?.tabs || [];
  if (tabs.length === 0) return null;

  return (
    <>
      <BannerSection />

      {/* DESKTOP VIEW */}
      {!isMobile && (
        <div className="container school_life pb_80">
          <div className="row">
            <div className="sec-title text-center">
              <h2>{sectionData.title || 'Student Life'}</h2>
            </div>

            <div className="col-md-3 mb-4 mb-md-0">
              <ul className="nav flex-column nav-pills custom-tabs" role="tablist">
                {tabs.map((tab) => (
                  <li key={tab.slug} className="nav-item mb-2">
                    <button
                      className={`nav-link w-100 text-start ${activeTab === tab.slug ? "active" : ""}`}
                      onClick={() => setActiveTab(tab.slug)}
                      type="button"
                      role="tab"
                    >
                      {tab.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-9">
              <div className="tab-content studentlifecontent">
                {tabs.map((tab) => (
                  <div
                    key={tab.slug}
                    className={`tab-pane fade ${activeTab === tab.slug ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    {renderTabContent(tab)}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* MOBILE VIEW */}
      {isMobile && (
        <div className="container school_life pb_80">
          <div className="sec-title text-center mb-3">
            <h2>{sectionData.title || 'Student Life'}</h2>
          </div>

          <div className="accordion" id="studentlifemobileAccordion">
            {tabs.map((tab, idx) => (
              <div className="accordion-item" key={tab.slug}>
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeTab === tab.slug ? "" : "collapsed"}`}
                    type="button"
                    onClick={() => setActiveTab(tab.slug)}
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-${idx}`}
                  >
                    {tab.name}
                  </button>
                </h2>

                <div
                  id={`collapse-${idx}`}
                  className={`accordion-collapse collapse ${activeTab === tab.slug ? "show" : ""}`}
                  data-bs-parent="#studentlifemobileAccordion"
                >
                  <div className="accordion-body">
                    {renderTabContent(tab)}
                  </div>
                </div>

              </div>
            ))}
          </div>

        </div>
      )}
    </>
  );
};

export default StudentLifeDetails;
